import { normalizeCustomFieldOptions, isRagColor } from './customFieldTypes';

/**
 * Client-side mirror of the server's DocumentRatingProcessor, used only to show
 * the user live totals and to decide when to prompt for a comment and photo.
 */

export const EMPTY_FINDINGS = { red: 0, amber: 0, green: 0 };

export const scoreChecklist = (field, formValue) => {
  const options = normalizeCustomFieldOptions(field.fieldRules?.options);
  if (options.length === 0) return EMPTY_FINDINGS;

  const selected = Array.isArray(formValue) ? formValue : [];
  if (selected.length === 0) return EMPTY_FINDINGS;

  const checked = options.filter((option) => selected.includes(option.id)).length;
  return { red: options.length - checked, amber: 0, green: checked };
};

export const getAnswerColor = (field, formValue) => {
  if (field.fieldType === 'Badge') {
    return isRagColor(formValue) ? String(formValue).toLowerCase() : undefined;
  }

  if (field.fieldType === 'Select' || field.fieldType === 'RadioButton') {
    if (typeof formValue !== 'string' || !formValue) return undefined;
    return normalizeCustomFieldOptions(field.fieldRules?.options).find(
      (option) => option.id === formValue
    )?.color;
  }

  if (field.fieldType === 'Checkbox') {
    const counts = scoreChecklist(field, formValue);
    if (counts.red === 0 && counts.green === 0) return undefined;
    return counts.red > 0 ? 'red' : 'green';
  }

  return undefined;
};

export const isEvidenceRequired = (color) => color === 'red' || color === 'amber';

export const DOCUMENT_TYPE_INSPECTION = 'Inspection';

export const isInspectionType = (typeName) =>
  (typeName ?? '').trim().toLowerCase() === DOCUMENT_TYPE_INSPECTION.toLowerCase();

const DRAFT_STATUS_NAME = 'In Progress';
const COMPLETE_STATUS_NAME = 'Completed';

/**
 * The two statuses the Save Draft and Complete buttons write.
 */
export const resolveInspectionStatuses = (statuses = []) => {
  const ordered = [...statuses].sort((a, b) => (a.statusOrder ?? 0) - (b.statusOrder ?? 0));
  const byName = (name) =>
    ordered.find((status) => status.statusName?.trim().toLowerCase() === name.toLowerCase());

  return {
    draft: byName(DRAFT_STATUS_NAME) ?? ordered.find((s) => !s.excludeFromActiveList),
    complete: byName(COMPLETE_STATUS_NAME) ?? ordered.find((s) => s.excludeFromActiveList),
  };
};

// ---- Evidence ----

const evidenceRule = (field, kind) => field.fieldRules?.evidence?.[kind] ?? 'off';

export const fieldSupportsEvidence = (field) =>
  evidenceRule(field, 'image') !== 'off' || evidenceRule(field, 'comment') !== 'off';

export const fieldCollectsImage = (field) => evidenceRule(field, 'image') !== 'off';

export const fieldCollectsComment = (field) => evidenceRule(field, 'comment') !== 'off';

export const fieldRequiresImage = (field) => evidenceRule(field, 'image') === 'required';

export const fieldRequiresComment = (field) => evidenceRule(field, 'comment') === 'required';

export const calculateFindings = (fields, getValue) =>
  fields.reduce((totals, field) => {
    if (field.fieldType === 'Checkbox') {
      const counts = scoreChecklist(field, getValue(field));
      return {
        red: totals.red + counts.red,
        amber: totals.amber + counts.amber,
        green: totals.green + counts.green,
      };
    }

    const color = getAnswerColor(field, getValue(field));
    if (!color) return totals;

    return { ...totals, [color]: totals[color] + 1 };
  }, EMPTY_FINDINGS);
